import { Fragment, ReactNode, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
	AppButton,
	AppIconButton,
	FilterPill,
	IconButton,
	Input,
	IntegrationLogo,
	Label,
	Pagination,
	DotMatrix,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tooltip
} from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import { useAccountStore, useAssetStore, useUiStore, useUserStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import SearchSm from '@untitled-ui/icons-react/build/esm/SearchSm'
import Settings01 from '@untitled-ui/icons-react/build/esm/Settings01'
import InfoCircle from '@untitled-ui/icons-react/build/esm/InfoCircle'
import { ModalTypes } from 'components/basic-blocks/g-modal/types'
import { ConnectAdAccountComponent } from 'components/setup-v2/connect-ad-account-component'
import { GEmpty } from 'components/basic-blocks/g-empty'
import {
	CampaignDTO,
	getCampaignStatusGroup
} from 'components/breadcrumb-bar/campaign-status'
import {
	ownsProtection,
	protectionToSettings
} from 'components/protection-settings/protection-adapters'
import {
	ConnectionsFilters,
	AD_ACCOUNT_FILTER_GROUPS,
	ConnectionsFilterState
} from './connections-filters'
import {
	adAccountLabel,
	connectionStatusView,
	formatAccountId,
	hasIssue,
	hasTrackingIssue,
	idCollator,
	ignoredCampaignIds,
	platformToProvider,
	protectionFilterValue
} from './connection-helpers'
import { ProtectionToggleCell } from './protection-toggle-cell'
import { CampaignDetectionToggle } from './campaign-detection-toggle'
import { CampaignStatusMark } from './campaign-status-mark'
import { AdAccountDrawer } from './ad-account-drawer'
import { CampaignDrawer } from './campaign-drawer'
import { SortHeadCell, SortState, nextSortState } from './sort-head-cell'

/**
 * Connections › Ad accounts (DEV-2422 §2.1–2.3).
 *
 * ONE table, not a table per nest. Campaign rows are rows of the same `<table>` as their
 * ad account, grouped by a `<Fragment>` — so a campaign's ID cell IS the parent's ID
 * column and the two can never drift. A nested second table was tried first and spent
 * its life chasing the parent's column widths, because each table distributes its own
 * surplus.
 */

/** The caret slot a child row skips, plus the gap after it. */
const CHILD_CARET_SLOT = 24 + 12
/** Holds the status disc where the parent's platform logo sits. */
const CHILD_ICON_SLOT = 20
/** Rows a nest shows before it earns a pager. */
const NESTED_PAGE_SIZE = 10

/**
 * The chevron lives INSIDE the Name cell rather than in a column of its own — that is
 * what lets a child row drop it and still start its name on the parent's x.
 */
const CaretToggle = ({
	expanded,
	label,
	onToggle
}: {
	expanded: boolean
	label: string
	onToggle: () => void
}) => (
	<AppIconButton
		variant="secondary"
		size="mini"
		aria-label={label}
		aria-expanded={expanded}
		onClick={onToggle}
		icon={
			<svg
				viewBox="0 0 8 8"
				aria-hidden
				className={`h-3 w-3 transition-transform duration-150 ${
					expanded ? 'rotate-90' : ''
				}`}
			>
				<path d="M2.5 1 L6 4 L2.5 7 Z" fill="currentColor" />
			</svg>
		}
	/>
)

/** Replaced the Connection status column (D9, Esteban 2026-08-11). Red only for a
 *  credential the user has to fix; a syncing or invited account stays amber and says
 *  which state it is in. */
const IssueLabel = ({ adAccount }: { adAccount: AdAccountDTO }) => {
	const connection = connectionStatusView(adAccount)
	if (connection)
		return (
			<Label size="md" color={connection.color}>
				{connection.text}
			</Label>
		)
	if (hasTrackingIssue(adAccount))
		return (
			<Label size="md" color="warning">
				Tracking issues
			</Label>
		)
	return null
}

interface Column {
	id: string
	header: string
	align?: 'right'
	sortable?: boolean
	cell: (row: AdAccountDTO) => ReactNode
	/**
	 * The campaign row's cell in this same column. Omit it to leave the child cell
	 * empty; return `null` to leave one cell empty while the column still fills others,
	 * which is what Click detection does for an account that is off.
	 */
	childCell?: (campaign: CampaignDTO, row: AdAccountDTO) => ReactNode
	headClassName?: string
	headAccessory?: ReactNode
}

export const AdAccountsTab = () => {
	const user = useUserStore((s) => s.user)
	const { asset, adAccounts, campaigns, demoMode, refreshAdAccounts } =
		useAssetStore(
			useShallow((s) => ({
				asset: s.asset,
				adAccounts: s.adAccounts,
				campaigns: s.campaigns,
				demoMode: s.demoMode,
				refreshAdAccounts: s.refreshAdAccounts
			}))
		)
	const { account, userAccess } = useAccountStore(
		useShallow((s) => ({ account: s.account, userAccess: s.userAccess }))
	)
	const setModalContent = useUiStore((s) => s.setModalContent)
	const setModalState = useUiStore((s) => s.setModalState)

	const [searchParams, setSearchParams] = useSearchParams()
	const [search, setSearch] = useState('')
	const [filters, setFilters] = useState<ConnectionsFilterState>({})
	const [pendingOnly, setPendingOnly] = useState(false)
	const [selected, setSelected] = useState<AdAccountDTO | null>(null)
	const [drawerOpen, setDrawerOpen] = useState(false)
	const [drawerTab, setDrawerTab] = useState<'setup' | 'protection'>('setup')
	const [selectedCampaign, setSelectedCampaign] = useState<{
		campaign: CampaignDTO
		adAccount: AdAccountDTO
	} | null>(null)
	const [campaignDrawerOpen, setCampaignDrawerOpen] = useState(false)
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(10)
	const [sort, setSort] = useState<SortState>({ key: 'account', dir: 'asc' })
	// "Multiple accounts can be expanded at once" (§2.2). A Set, not one id: an earlier
	// round closed the open nest whenever another was opened, which made comparing two
	// accounts' campaigns impossible.
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
	// Per account, because the pager belongs to the nest and not to the page (§2.2).
	const [campaignPage, setCampaignPage] = useState<Record<string, number>>({})
	const [campaignSize, setCampaignSize] = useState<Record<string, number>>({})
	/**
	 * The entity a deep link asked us to open, held until the store can answer it
	 * (§1.1 — the AI Protection page's "{n} with their own settings" panel links here).
	 *
	 * Kept in state rather than read straight off the URL: the rows arrive after this
	 * mounts, and the ad accounts land one render before their campaigns do, so the
	 * request has to outlive the params. The params themselves are cleared on sight, so
	 * closing the drawer cannot reopen it and a refresh does not either.
	 */
	const [pendingOpen, setPendingOpen] = useState<{
		adAccountId: string
		campaignId: string | null
	} | null>(null)

	const isLoading = adAccounts == null
	const canWrite = !demoMode && userAccess(user?.id || '') !== 'READ'
	const query = search.trim().toLowerCase()

	/**
	 * Ad accounts the search matched on their OWN name or id.
	 *
	 * Their campaigns are then exempt from the text filter below. The account IS the
	 * result, so narrowing its children by the same string hid every one of them and
	 * took the expander with them — searching "Gads" returned ClickGUARD Gads with no
	 * chevron and no way to reach its two campaigns.
	 */
	const accountsMatchingQuery = useMemo(() => {
		const ids = new Set<string>()
		if (!query) return ids
		;(adAccounts ?? []).forEach((a) => {
			if (
				adAccountLabel(a).toLowerCase().includes(query) ||
				formatAccountId(a).toLowerCase().includes(query)
			)
				ids.add(a.id)
		})
		return ids
	}, [adAccounts, query])

	/**
	 * Campaigns per ad account, already narrowed by the search and the status filter.
	 * No fetch: the store loads every campaign for every ad account with the asset.
	 */
	const campaignsByAccount = useMemo(() => {
		const picked = filters.campaignStatuses ?? []
		// Enabled only until the user says otherwise (§2.2).
		const wanted = picked.length ? picked : ['ACTIVE']
		const byAccount = new Map<string, CampaignDTO[]>()
		;(campaigns ?? []).forEach((entry) => {
			const campaign = entry as CampaignDTO
			const group = getCampaignStatusGroup(campaign)
			if (!group || !wanted.includes(group)) return
			// The status filter always applies; the TEXT filter does not, once the parent
			// account itself is what the query matched.
			if (
				query &&
				!accountsMatchingQuery.has(campaign.ad_account_id) &&
				!campaign.name?.toLowerCase().includes(query) &&
				!campaign.id.toLowerCase().includes(query)
			)
				return
			const bucket = byAccount.get(campaign.ad_account_id) ?? []
			bucket.push(campaign)
			byAccount.set(campaign.ad_account_id, bucket)
		})
		return byAccount
	}, [campaigns, filters.campaignStatuses, query, accountsMatchingQuery])

	const campaignsOf = (row: AdAccountDTO) =>
		campaignsByAccount.get(row.id) ?? []

	const { rows, pendingCount } = useMemo(() => {
		let list: AdAccountDTO[] = adAccounts ?? []

		// Search spans the account AND its campaigns (§2.1), so an account stays when the
		// only hit is one level down.
		if (query)
			list = list.filter(
				(a) =>
					adAccountLabel(a).toLowerCase().includes(query) ||
					formatAccountId(a).toLowerCase().includes(query) ||
					(campaignsByAccount.get(a.id)?.length ?? 0) > 0
			)

		if (filters.platforms?.length)
			list = list.filter((a) => filters.platforms.includes(a.platform))
		if (filters.protectionStatuses?.length)
			list = list.filter((a) =>
				filters.protectionStatuses.includes(protectionFilterValue(a))
			)
		// A campaign-status pick acts on the CHILD rows, so an account survives when at
		// least one of its campaigns matches.
		if (filters.campaignStatuses?.length)
			list = list.filter((a) => (campaignsByAccount.get(a.id)?.length ?? 0) > 0)

		const count = list.filter(hasIssue).length
		return {
			rows: pendingOnly ? list.filter(hasIssue) : list,
			pendingCount: count
		}
	}, [adAccounts, query, filters, pendingOnly, campaignsByAccount])

	const sortedRows = useMemo(() => {
		if (!sort) return rows
		const value = (row: AdAccountDTO) =>
			sort.key === 'accountId'
				? formatAccountId(row)
				: sort.key === 'protection'
				? protectionFilterValue(row)
				: adAccountLabel(row)
		return [...rows].sort(
			(a, b) =>
				idCollator.compare(value(a), value(b)) * (sort.dir === 'desc' ? -1 : 1)
		)
	}, [rows, sort])

	const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize))
	useEffect(() => {
		if (page > totalPages) setPage(totalPages)
	}, [page, totalPages])

	const pagedRows = useMemo(
		() => sortedRows.slice((page - 1) * pageSize, page * pageSize),
		[sortedRows, page, pageSize]
	)

	// Accounts a filter or search has just dropped must not stay open underneath.
	// Returns the SAME Set when nothing was dropped — a fresh one every render would
	// re-run this effect forever.
	useEffect(() => {
		setExpandedIds((current) => {
			if (!current.size) return current
			const visible = new Set(sortedRows.map((row) => row.id))
			const next = new Set(Array.from(current).filter((id) => visible.has(id)))
			return next.size === current.size ? current : next
		})
	}, [sortedRows])

	const toggleExpanded = (id: string) =>
		setExpandedIds((current) => {
			const next = new Set(current)
			if (!next.delete(id)) next.add(id)
			return next
		})

	/** Counts per filter option, each group counting with its OWN picks ignored. */
	const optionCounts = useMemo(() => {
		const base = (adAccounts ?? []).filter(
			(a) =>
				!query ||
				adAccountLabel(a).toLowerCase().includes(query) ||
				formatAccountId(a).toLowerCase().includes(query) ||
				(campaignsByAccount.get(a.id)?.length ?? 0) > 0
		)
		const platforms: Record<string, number> = {}
		const detection: Record<string, number> = { ON: 0, OFF: 0 }
		base.forEach((a) => {
			if (
				!filters.protectionStatuses?.length ||
				filters.protectionStatuses.includes(protectionFilterValue(a))
			)
				platforms[a.platform] = (platforms[a.platform] ?? 0) + 1
			if (!filters.platforms?.length || filters.platforms.includes(a.platform))
				detection[protectionFilterValue(a)] += 1
		})
		// Campaign status counts CAMPAIGNS — it is the child rows it acts on.
		const campaignStatus: Record<string, number> = {
			ACTIVE: 0,
			PAUSED: 0,
			REMOVED: 0
		}
		;(campaigns ?? []).forEach((entry) => {
			const group = getCampaignStatusGroup(entry as CampaignDTO)
			if (group) campaignStatus[group] += 1
		})
		return { platforms, detection, campaignStatus }
	}, [adAccounts, campaigns, campaignsByAccount, filters, query])

	const filterGroups = useMemo(
		() =>
			AD_ACCOUNT_FILTER_GROUPS.map((group) => ({
				...group,
				options: group.options.map((option) => ({
					...option,
					count:
						group.key === 'platforms'
							? optionCounts.platforms[option.value] ?? 0
							: group.key === 'campaignStatuses'
							? optionCounts.campaignStatus[option.value] ?? 0
							: optionCounts.detection[option.value] ?? 0
				}))
			})),
		[optionCounts]
	)

	const handleConnect = () => {
		if (!account || !asset || !user) return
		setModalContent({
			type: ModalTypes.INFO,
			actionsEnabled: false,
			showIcon: false,
			rootClassName: 'bg-transparent',
			body: (
				<ConnectAdAccountComponent
					account={account}
					isOnboarding={false}
					user={user}
					asset={asset}
					onSuccess={async () => {
						await refreshAdAccounts()
						setModalState(false)
					}}
				/>
			)
		})
	}

	// `tab` is only ever passed by the deep link (§1.1) — the gear icon opens Setup.
	const openDrawer = (
		adAccount: AdAccountDTO,
		tab: 'setup' | 'protection' = 'setup'
	) => {
		setSelected(adAccount)
		setDrawerTab(tab)
		setDrawerOpen(true)
	}

	const openCampaignDrawer = (
		campaign: CampaignDTO,
		adAccount: AdAccountDTO
	) => {
		setSelectedCampaign({ campaign, adAccount })
		setCampaignDrawerOpen(true)
	}

	// Take the deep link off the URL immediately. It is a one-shot instruction, not
	// state the page should keep: left in place it would reopen the drawer on every
	// close, and survive into a shared or bookmarked link.
	useEffect(() => {
		const adAccountId = searchParams.get('adAccount')
		if (!adAccountId) return
		setPendingOpen({ adAccountId, campaignId: searchParams.get('campaign') })
		const next = new URLSearchParams(searchParams)
		next.delete('adAccount')
		next.delete('campaign')
		setSearchParams(next, { replace: true })
	}, [searchParams, setSearchParams])

	// Then open it as soon as the rows exist. Re-runs on every store update until it
	// lands, because `adAccounts` is null on the first pass and `campaigns` is filled a
	// render later (`refreshCampaigns` runs after `setAdAccounts`).
	useEffect(() => {
		if (!pendingOpen) return
		const target = (adAccounts ?? []).find(
			(row) => row.id === pendingOpen.adAccountId
		)
		if (!target) return

		if (pendingOpen.campaignId) {
			const campaign = (campaigns ?? []).find(
				(entry) => entry.id === pendingOpen.campaignId
			) as CampaignDTO | undefined
			if (!campaign) return
			// The row the drawer opened on should be behind it. The status filter defaults
			// to Enabled only (§2.2), so a paused or removed campaign needs its own bucket
			// added — never removing one the user picked.
			const group = getCampaignStatusGroup(campaign)
			if (group && group !== 'ACTIVE')
				setFilters((current) => {
					const picked = current.campaignStatuses?.length
						? current.campaignStatuses
						: ['ACTIVE']
					return picked.includes(group)
						? current
						: { ...current, campaignStatuses: [...picked, group] }
				})
			setExpandedIds((current) => new Set(current).add(target.id))
			openCampaignDrawer(campaign, target)
		} else {
			// Straight to Protection: the panel that linked here lists this account
			// BECAUSE it owns its protection settings (§1.1).
			openDrawer(target, 'protection')
		}

		// The account can be on any page of the table; bring it into view so closing the
		// drawer does not land on an unrelated page.
		const index = sortedRows.findIndex((row) => row.id === target.id)
		if (index >= 0) setPage(Math.floor(index / pageSize) + 1)

		setPendingOpen(null)
	}, [pendingOpen, adAccounts, campaigns, sortedRows, pageSize])

	/** The campaign drawer's parent, re-read from the store on every render. */
	const liveCampaignAccount = selectedCampaign
		? adAccounts?.find((a) => a.id === selectedCampaign.adAccount.id) ??
		  selectedCampaign.adAccount
		: null

	// One step up the chain, resolved HERE because only the table knows whether the ad
	// account owns its settings or is passing the website's straight through.
	const websiteSettings = protectionToSettings(asset?.protection)
	const effectiveParent = (adAccount: AdAccountDTO) =>
		ownsProtection(adAccount)
			? protectionToSettings(adAccount.protection)
			: websiteSettings

	const columns: Column[] = [
		{
			id: 'account',
			// "Name", not "Account": the column carries an account name on a parent row
			// and a CAMPAIGN name on a child row.
			header: 'Name',
			sortable: true,
			// The table is auto-layout, so a column takes the width of its widest cell and
			// the short "Name" header loses to its neighbours. The floor gives the name
			// its width back once the caret slot is taken out of it.
			headClassName: 'min-w-[320px]',
			cell: (row) => {
				const open = expandedIds.has(row.id)
				return (
					<div className="flex items-center gap-3">
						{/* The caret slot is the same width on every row, with campaigns or
						    without, so all account names start on the same x. */}
						{campaignsOf(row).length > 0 ? (
							<CaretToggle
								expanded={open}
								label={`${
									open ? 'Hide' : 'Show'
								} campaigns for ${adAccountLabel(row)}`}
								onToggle={() => toggleExpanded(row.id)}
							/>
						) : (
							<div className="w-6 shrink-0" aria-hidden />
						)}
						<IntegrationLogo
							provider={platformToProvider(row.platform)}
							size={20}
						/>
						<span className="truncate text-sm font-medium text-t-default">
							{adAccountLabel(row)}
						</span>
					</div>
				)
			},
			childCell: (campaign, row) => (
				// The campaign takes the slot the account gives to its platform logo: that
				// slot carries the campaign's own state, which is the one fact the child
				// row could not otherwise tell you. The platform is on the account above.
				<div
					className="flex items-center gap-3"
					style={{ paddingLeft: CHILD_CARET_SLOT }}
				>
					<span
						className="flex shrink-0 justify-center"
						style={{ width: CHILD_ICON_SLOT }}
					>
						<CampaignStatusMark campaign={campaign} platform={row.platform} />
					</span>
					<span className="block truncate text-sm text-t-default">
						{campaign.name}
					</span>
				</div>
			)
		},
		{
			id: 'accountId',
			// "ID" for the same reason as "Name": a child row shows a CAMPAIGN id.
			header: 'ID',
			sortable: true,
			// `whitespace-nowrap`: the Name column carries a 320px floor, so the
			// auto-layout table takes the slack out of this one and breaks
			// `964-956-9197` across two lines at a hyphen. An ID is one token.
			cell: (row) => (
				<span className="whitespace-nowrap text-sm text-t-secondary">
					{formatAccountId(row)}
				</span>
			),
			childCell: (campaign) => (
				<span className="whitespace-nowrap text-sm text-t-secondary">
					{campaign.id}
				</span>
			)
		},
		{
			id: 'protection',
			header: 'Click detection',
			sortable: true,
			headAccessory: (
				<Tooltip
					content="Click detection"
					description="While this is off, new clicks are not flagged or blocked and the account's spend does not count toward your monthly usage."
					portal
				>
					<InfoCircle className="h-3 w-3 text-t-secondary" />
				</Tooltip>
			),
			cell: (row) => <ProtectionToggleCell adAccount={row} />,
			// An account that is off stops every campaign under it, so the toggles are
			// HIDDEN, not disabled, and no campaign's stored state changes (§2.3). The
			// cell stays so the column keeps its width.
			childCell: (campaign, row) =>
				row.status === 'ACTIVE' ? (
					<CampaignDetectionToggle
						adAccount={row}
						campaign={campaign}
						canWrite={canWrite}
					/>
				) : null
		},
		{
			id: 'actions',
			header: '',
			align: 'right',
			cell: (row) => (
				<div className="flex items-center justify-end gap-2">
					<IssueLabel adAccount={row} />
					<IconButton
						size="sm"
						color="gray"
						aria-label="Ad account settings"
						icon={<Settings01 className="h-4 w-4" />}
						onClick={() => openDrawer(row)}
					/>
				</div>
			),
			childCell: (campaign, row) => (
				<div className="flex items-center justify-end">
					<IconButton
						size="sm"
						color="gray"
						aria-label="Campaign settings"
						icon={<Settings01 className="h-4 w-4" />}
						onClick={() => openCampaignDrawer(campaign, row)}
					/>
				</div>
			)
		}
	]

	return (
		<div className="flex flex-col">
			<div className="flex flex-col gap-4 pt-4">
				<div className="flex items-center justify-between gap-3">
					<div className="flex items-center gap-[12px]">
						<Input
							state="default"
							size="sm"
							placeholder="Search"
							leadingIcon={<SearchSm />}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<ConnectionsFilters
							groups={filterGroups}
							value={filters}
							onChange={setFilters}
						/>
						{pendingCount > 0 && (
							<FilterPill
								label="Pending issues"
								count={pendingCount}
								pressed={pendingOnly}
								onPressedChange={setPendingOnly}
							/>
						)}
					</div>
					<AppButton
						variant="primary"
						size="sm"
						label="Connect new account"
						onClick={handleConnect}
						disabled={!canWrite}
					/>
				</div>

				<div className="overflow-x-auto">
					<Table>
						<TableHeader className="cg-bg-surface-2">
							<TableRow className="hover:!bg-transparent">
								{columns.map((col) =>
									col.sortable ? (
										<SortHeadCell
											key={col.id}
											label={col.header}
											sortKey={col.id}
											sort={sort}
											onSort={(key) => setSort(nextSortState(sort, key))}
											align={col.align}
											className={col.headClassName}
										>
											{col.headAccessory}
										</SortHeadCell>
									) : (
										<TableHead
											key={col.id}
											className={[
												col.align === 'right' ? 'text-right' : '',
												col.headClassName ?? ''
											]
												.join(' ')
												.trim()}
										>
											{col.header}
										</TableHead>
									)
								)}
							</TableRow>
						</TableHeader>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={columns.length}>
										<div className="flex justify-center py-16">
											<DotMatrix label="Loading ad accounts" />
										</div>
									</TableCell>
								</TableRow>
							) : sortedRows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={columns.length}>
										<GEmpty
											title="No ad accounts"
											description="No ad accounts under this website yet."
										/>
									</TableCell>
								</TableRow>
							) : (
								pagedRows.map((row) => {
									const open = expandedIds.has(row.id)
									// The nest shows only what the search and the Campaign status
									// group keep, so the pager, the closing line and the rows all
									// count the same set.
									const allCampaigns = campaignsOf(row)
									// The gate is the DEFAULT size, not the current one: below it
									// the nest shows every campaign and no pager at all.
									const paginates = allCampaigns.length > NESTED_PAGE_SIZE
									const size = campaignSize[row.id] ?? NESTED_PAGE_SIZE
									const nestPages = Math.max(
										1,
										Math.ceil(allCampaigns.length / size)
									)
									// Clamp: raising rows-per-page can drop the page count below
									// the page you were on.
									const nestPage = Math.min(
										campaignPage[row.id] ?? 1,
										nestPages
									)
									const start = (nestPage - 1) * size
									const shown = paginates
										? allCampaigns.slice(start, start + size)
										: allCampaigns

									return (
										<Fragment key={row.id}>
											<TableRow>
												{columns.map((col) => (
													<TableCell
														key={col.id}
														truncate={col.id !== 'accountId'}
														className={
															col.align === 'right' ? 'text-right' : undefined
														}
													>
														{col.cell(row)}
													</TableCell>
												))}
											</TableRow>
											{shown.map((campaign, childIndex) => {
												// The LAST item of a block owns a 2px closing line,
												// which takes the place of the divider the row drops.
												// It is what tells you the nest ended, not a gap. When
												// the nest paginates the PAGER is last, so the line
												// moves down to it.
												const endsNest =
													!paginates && childIndex === shown.length - 1
												return (
													<TableRow
														// POSITIONAL key: a page change then swaps the
														// content of stable slots instead of unmounting
														// the rows, so the nest does not collapse and
														// regrow.
														key={`${row.id}-slot-${childIndex}`}
														// A table row can only collapse by shrinking its
														// cell CONTENT, so the animated wrapper lives
														// inside each `td`, never on the `tr`. The row
														// drops the DS divider, which would draw a line
														// even at zero height.
														className="!border-b-0 bg-[rgb(var(--cg-surface-subtle))]"
													>
														{columns.map((col) => (
															// `relative` gives the closing line below its
															// positioning context.
															<TableCell
																key={col.id}
																truncate={col.id !== 'accountId'}
																className="relative !p-0"
															>
																{/* The collapse is the CSS grid trick:
																    `0fr` to `1fr` animates, `height: auto`
																    does not. The padding sits inside the
																    clipped child, so it collapses too. */}
																<div
																	className="grid transition-[grid-template-rows] duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
																	style={{
																		gridTemplateRows: open ? '1fr' : '0fr'
																	}}
																	aria-hidden={!open}
																>
																	<div className="overflow-hidden">
																		{/* `py-4`, not `py-3`: a child row is the
																		    same height as the parent above it,
																		    which is the DS cell padding. */}
																		<div
																			className={`px-6 py-4 ${
																				col.align === 'right'
																					? 'text-right'
																					: ''
																			}`}
																		>
																			{col.childCell?.(campaign, row)}
																		</div>
																	</div>
																</div>
																{/* Drawn per cell, not per row: a `tr` cannot
																    hold an absolutely positioned child. It
																    only exists while the nest is open, so a
																    collapsed block leaves no line behind. */}
																{endsNest && open && (
																	<div
																		aria-hidden
																		className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-[rgb(var(--cg-brand))]"
																	/>
																)}
															</TableCell>
														))}
													</TableRow>
												)
											})}
											{paginates && (
												// The in-nest pager: one full-width cell holding the
												// same DS Pagination the table footer uses (§2.2).
												<TableRow
													key={`${row.id}-pager`}
													className="!border-b-0 bg-[rgb(var(--cg-surface-subtle))]"
												>
													<TableCell
														colSpan={columns.length}
														className="relative !p-0"
													>
														<div
															className="grid transition-[grid-template-rows] duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
															style={{
																gridTemplateRows: open ? '1fr' : '0fr'
															}}
															aria-hidden={!open}
														>
															<div className="overflow-hidden">
																<div className="px-6 py-2.5">
																	<Pagination
																		page={nestPage}
																		totalPages={nestPages}
																		onPageChange={(next) =>
																			setCampaignPage((prev) => ({
																				...prev,
																				[row.id]: next
																			}))
																		}
																		pageSize={size}
																		pageSizeOptions={[10, 20, 50]}
																		onPageSizeChange={(next) => {
																			setCampaignSize((prev) => ({
																				...prev,
																				[row.id]: next
																			}))
																			setCampaignPage((prev) => ({
																				...prev,
																				[row.id]: 1
																			}))
																		}}
																	/>
																</div>
															</div>
														</div>
														{open && (
															<div
																aria-hidden
																className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-[rgb(var(--cg-brand))]"
															/>
														)}
													</TableCell>
												</TableRow>
											)}
										</Fragment>
									)
								})
							)}
						</TableBody>
					</Table>
				</div>
			</div>
			{!isLoading && sortedRows.length > 0 && (
				<Pagination
					page={page}
					totalPages={totalPages}
					onPageChange={setPage}
					pageSize={pageSize}
					pageSizeOptions={[10, 20, 50]}
					onPageSizeChange={(size) => {
						setPageSize(size)
						setPage(1)
					}}
				/>
			)}
			{!!selectedCampaign && !!liveCampaignAccount && (
				<CampaignDrawer
					campaign={selectedCampaign.campaign}
					// The live object, for the same reason the ad-account drawer below takes
					// one: the `selectedCampaign` snapshot goes stale after the drawer's own
					// save refreshes the accounts, and the ignored list it carries is what
					// the click-detection banner reads.
					adAccount={liveCampaignAccount}
					parentValues={effectiveParent(liveCampaignAccount)}
					// Both levels resolved into one answer: an ad account that is off stops
					// every campaign under it, and the table hides the campaign toggle in
					// that state — so the banner is the only place it can be explained.
					clickDetectionOn={
						liveCampaignAccount.status === 'ACTIVE' &&
						!ignoredCampaignIds(liveCampaignAccount).includes(
							selectedCampaign.campaign.id
						)
					}
					open={campaignDrawerOpen}
					onClose={() => setCampaignDrawerOpen(false)}
				/>
			)}
			{!!selected && (
				<AdAccountDrawer
					// Resolve the live object from the store on every render — the `selected`
					// snapshot goes stale after each save's refreshAdAccounts().
					adAccount={adAccounts?.find((a) => a.id === selected.id) ?? selected}
					open={drawerOpen}
					initialTab={drawerTab}
					onClose={() => setDrawerOpen(false)}
				/>
			)}
		</div>
	)
}
